import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useComponentTranslation } from "@/services/i18n";
import { countryLabel } from "@/constants/countries";
import toast from "react-hot-toast";
import {
  FiChevronRight,
  FiSearch,
  FiCheck,
  FiX,
  FiMail,
  FiLinkedin,
  FiUser,
  FiArrowRight,
  FiGlobe,
  FiGithub,
  FiPhone,
  FiChevronLeft,
  FiFileText,
  FiDownload,
  FiChevronUp,
  FiChevronDown,
  FiRotateCw,
  FiSettings,
} from "react-icons/fi";

import CandidateModal from "@/components/candidate-modal";
import Modal from "@/components/modal";
import LoadingButton from "@/components/loading-button";
import api from "@/services/api";
import { downloadResume } from "@/utils";
import useStore from "@/services/store";
import { capture } from "@/services/sentry";
import ScreeningBulkRun from "./ScreeningBulkRun";
import ScreeningCell from "./ScreeningCell";
import ScreeningProgressBadge from "./ScreeningProgressBadge";

const SOURCE_META = {
  AD: { dot: "bg-blue-500", chip: "bg-blue-50 text-blue-700", label: "Ad" },
  POOL: { dot: "bg-green-500", chip: "bg-green-50 text-green-700", label: "Pool" },
  HUNT: { dot: "bg-amber-500", chip: "bg-amber-50 text-amber-700", label: "Hunt" },
};

function SourceBadge({ source }) {
  const meta = SOURCE_META[source];
  if (!meta) return <span className="text-sm text-gray-300">—</span>;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium ${meta.chip}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}

const translations = {
  en: {
    search_placeholder: "Search for a candidate...",
    filter: "Filter",
    selected: "selected",
    move_to: "Move to",
    refuse: "Refuse",
    clear_all: "Clear All",
    no_candidates: "No candidates in this stage",
    automatic: "Automatic",
    manual: "Manual",
    screening_score: "Screening Score",
    matching_score: "Match",
    applied_at: "Applied on",
    source: "Source",
    name: "Name",
    email: "Email",
    school: "School",
    last_experience: "Last Experience",
    city: "City",
    country: "Country",
    social: "Social",
    contact: "Contact",
    metrics: {
      views: "Views",
      applications: "Applications",
      conversion: "Conversion",
      see_more: "See more analytics",
    },
    create_stage: "Create Stage",
    new_stage_name: "New Stage",
    stage_created: "Stage created successfully",
    error_creating_stage: "Error creating stage",
    add_stage_here: "Add stage here",
    import_candidate: "Import Candidate",
    message_candidate: "Send Message",
    message_subject: "Subject",
    message_body: "Message",
    send_message: "Send",
    cancel: "Cancel",
    message_sent: "Message sent successfully",
    messages_sent: "Messages sent successfully to {count} candidates",
    error_sending_message: "Error sending message",
    subject_required: "Subject is required",
    message_required: "Message is required",
    sending_to_multiple: "Sending to {count} candidates",
    select_all_candidates: "Select all {count} candidates",
    all_candidates_selected: "All {count} candidates selected",
    clear_selection: "Clear selection",
    export: "Export",
    tab_all: "All",
    tab_ad: "Ad",
    tab_pool: "Pool",
    tab_hunt: "Hunt",
    candidates_loaded: "{loaded} of {total}",
    loading_more: "Loading more…",
    end_of_list: "End of list",
  },
  fr: {
    search_placeholder: "Rechercher un candidat...",
    filter: "Filtrer",
    selected: "sélectionné(s)",
    move_to: "Déplacer vers",
    refuse: "Refuser",
    clear_all: "Tout effacer",
    no_candidates: "Aucun candidat à cette étape",
    automatic: "Automatique",
    manual: "Manuel",
    screening_score: "Score IA",
    matching_score: "Match",
    applied_at: "Date",
    source: "Source",
    name: "Nom",
    email: "Email",
    school: "École",
    last_experience: "Dernière Expérience",
    city: "Ville",
    country: "Pays",
    social: "Social",
    contact: "Contact",
    metrics: {
      views: "Vues",
      applications: "Candidatures",
      conversion: "Conversion",
      see_more: "Voir plus d'analyses",
    },
    create_stage: "Créer une étape",
    new_stage_name: "Nouvelle étape",
    stage_created: "Étape créée avec succès",
    error_creating_stage: "Erreur lors de la création de l'étape",
    add_stage_here: "Ajouter une étape ici",
    import_candidate: "Importer un candidat",
    message_candidate: "Envoyer un message",
    message_subject: "Sujet",
    message_body: "Message",
    send_message: "Envoyer",
    cancel: "Annuler",
    message_sent: "Message envoyé avec succès",
    messages_sent: "Messages envoyés avec succès à {count} candidats",
    error_sending_message: "Erreur lors de l'envoi du message",
    subject_required: "Le sujet est requis",
    message_required: "Le message est requis",
    sending_to_multiple: "Envoi à {count} candidats",
    select_all_candidates: "Sélectionner les {count} candidats",
    all_candidates_selected: "Tous les {count} candidats sont sélectionnés",
    clear_selection: "Effacer la sélection",
    export: "Exporter",
    tab_all: "Tous",
    tab_ad: "Annonce",
    tab_pool: "Vivier",
    tab_hunt: "Hunt",
    candidates_loaded: "{loaded} sur {total}",
    loading_more: "Chargement…",
    end_of_list: "Fin de liste",
  },
};

const DEFAULT_STAGES = [
  { name: "Application", key: "APPLIED", type: "manual", count: 0, color: "indigo" },
  { name: "Selected", key: "SELECTED", type: "manual", count: 0, color: "emerald" },
  { name: "Refused", key: "REFUSED", type: "manual", count: 0, color: "red" },
];

function timeAgo(date) {
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);

  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
  };

  for (const [unit, secondsInUnit] of Object.entries(intervals)) {
    const interval = Math.floor(seconds / secondsInUnit);
    if (interval >= 1) {
      return `${interval} ${unit}${interval !== 1 ? "s" : ""} ago`;
    }
  }

  return "just now";
}

const PAGE_SIZE = 50;

export default function Candidates({ job, setJob, refreshTrigger, posts = [] }) {
  const { t } = useComponentTranslation(translations);
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [statusCounts, setStatusCounts] = useState([]);
  const [sourceCountsAgg, setSourceCountsAgg] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedStageIndex, setSelectedStageIndex] = useState(-1);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedCandidateUser, setSelectedCandidateUser] = useState(null);
  const [stages, setStages] = useState(DEFAULT_STAGES);
  // Bumped whenever onRefresh runs — lets ScreeningProgressBadge re-check status
  const [screeningPulse, setScreeningPulse] = useState(0);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [candidatesToMessage, setCandidatesToMessage] = useState([]);
  const [sort, setSort] = useState({ field: "created_at", order: "desc" });
  const [verdictFilter, setVerdictFilter] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Scroll restoration for candidate modal open/close
  const scrollRef = useRef(null);
  const savedScrollRef = useRef(0);
  // Drops responses from a previous All/Ad/Pool/Hunt click when a newer one is in flight.
  const listRequestRef = useRef(0);
  const queryKeyRef = useRef("");
  const loadedCountRef = useRef(0);

  const [searchParams, setSearchParams] = useSearchParams();
  const candidateIdFromUrl = searchParams.get("candidateId");

  useEffect(() => {
    if (candidateIdFromUrl && candidates.length > 0) {
      const candidate = candidates.find((c) => c._id === candidateIdFromUrl);
      if (candidate) {
        setSelectedApplication(candidate);
      }
    }
  }, [candidateIdFromUrl, candidates]);

  // Fetch stages separately
  useEffect(() => {
    const fetchStages = async () => {
      try {
        const { ok, data } = await api.post("/stage/search", { job_id: id });
        if (ok && data.length > 0) {
          setStages(data);
        } else {
          setStages(DEFAULT_STAGES);
        }
      } catch (e) {
        console.error(e);
      }
    };
    if (id) fetchStages();
  }, [id]);

  const currentStage = stages[selectedStageIndex] || stages[0];

  // Debounce search input (250ms) — fires server-side query
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 250);
    return () => clearTimeout(h);
  }, [searchTerm]);

  const queryKey = [id, selectedStageIndex, sourceFilter, debouncedSearch, sort.field, sort.order, verdictFilter, stages.map((s) => s.key).join(",")].join("|");
  queryKeyRef.current = queryKey;

  // Reset & fetch page 1 whenever a filter changes
  useEffect(() => {
    if (!id || !stages.length) return;
    fetchPage(1, { reset: true, key: queryKey });
    fetchAggregates(queryKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  // External refresh
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchPage(1, { reset: true });
      fetchAggregates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  useEffect(() => {
    if (!selectedApplication) {
      setSelectedCandidateUser(null);
      return;
    }

    const fetchCandidateUser = async () => {
      try {
        const { ok, data } = await api.get(`/user/${selectedApplication.candidate_id}`);
        if (ok) {
          setSelectedCandidateUser(data);
        }
      } catch (e) {
        console.error(e);
        toast.error("Error fetching candidate details");
      }
    };

    fetchCandidateUser();
  }, [selectedApplication]);

  // Build the server-side filter from current UI state.
  // Used both by paged fetch and by "select all matching" bulk action.
  const buildSearchBody = useCallback(() => {
    const body = {
      job_id: id,
      organization_id: job?.organization_id,
      sort_field: sort.field,
      sort_order: sort.order,
    };
    // Stage scoping: specific stage if one is selected, otherwise all known stage keys
    if (selectedStageIndex === -1) {
      body.status = stages.map((s) => s.key);
    } else if (currentStage?.key) {
      body.status = currentStage.key;
    }
    // Source
    if (sourceFilter && sourceFilter !== "all") {
      body.source = sourceFilter.toUpperCase();
    }
    // Search
    if (debouncedSearch) body.search = debouncedSearch;
    // Verdict / screening-score filter
    if (verdictFilter) {
      if (verdictFilter === "strong") body.screening_score_min = 80;
      else if (verdictFilter === "good") {
        body.screening_score_min = 60;
        body.screening_score_max = 80;
      } else if (verdictFilter === "weak") body.screening_score_max = 60;
    }
    return body;
  }, [id, job?.organization_id, sort.field, sort.order, selectedStageIndex, currentStage?.key, sourceFilter, debouncedSearch, verdictFilter, stages]);

  const isCurrentRequest = (requestId, key) => requestId === listRequestRef.current && key === queryKeyRef.current;

  const fetchPage = async (targetPage, { reset = false, key = queryKey } = {}) => {
    if (key !== queryKeyRef.current) return;
    const requestId = reset ? ++listRequestRef.current : listRequestRef.current;
    try {
      if (reset) {
        setLoading(true);
        setCandidates([]);
        setHasMore(false);
        loadedCountRef.current = 0;
      } else {
        setLoadingMore(true);
      }
      const body = { ...buildSearchBody(), page: targetPage, limit: PAGE_SIZE };
      const { ok, data, total: serverTotal } = await api.post("/application/search", body);
      if (!isCurrentRequest(requestId, key)) return;
      if (!ok) return toast.error("Error fetching candidates");
      const rows = data || [];
      const loadedCount = reset ? rows.length : loadedCountRef.current + rows.length;
      loadedCountRef.current = loadedCount;
      setCandidates((prev) => (reset ? rows : [...prev, ...rows]));
      setPage(targetPage);
      setTotal(typeof serverTotal === "number" ? serverTotal : loadedCount);
      setHasMore(typeof serverTotal === "number" ? loadedCount < serverTotal : rows.length === PAGE_SIZE);
    } catch (e) {
      if (!isCurrentRequest(requestId, key)) return;
      console.error(e);
      toast.error(e.code || "Error fetching candidates");
    } finally {
      if (isCurrentRequest(requestId, key)) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    fetchPage(page + 1, { key: queryKey });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, loadingMore, hasMore, page, queryKey]);

  const fetchAggregates = async (key = queryKey) => {
    try {
      const body = {
        job_id: id,
        status: stages.map((s) => s.key),
      };
      if (sourceFilter && sourceFilter !== "all") body.source = sourceFilter.toUpperCase();
      const { ok, data } = await api.post("/application/aggregate", body);
      if (key !== queryKeyRef.current) return;
      if (ok) {
        setStatusCounts(data.status || []);
        setSourceCountsAgg(data.source || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Fetch every matching ID without paging — used by "Select all N" bulk path.
  const fetchAllMatchingIds = async () => {
    const body = { ...buildSearchBody(), page: 1, limit: Math.max(total, 1) };
    try {
      const { ok, data } = await api.post("/application/search", body);
      if (!ok) return [];
      return (data || []).map((c) => c._id);
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  const handleUpdateCandidates = async (candidateIds, updates) => {
    // Normalize to array
    const ids = Array.isArray(candidateIds) ? candidateIds : [candidateIds];

    try {
      // 1. Update API (Parallel requests)
      // Note: Ideally we should have a bulk update endpoint
      await Promise.all(ids.map((id) => api.put(`/application/${id}`, updates)));

      // 2. Optimistic Local Update
      setCandidates((prev) => prev.map((c) => (ids.includes(c._id) ? { ...c, ...updates } : c)));

      // 3. Handle specific side effects
      if (updates.status) {
        // Status changed → applications may have left the current stage view.
        // Refresh counts and drop moved-out rows from the visible list.
        fetchAggregates();
        if (selectedStageIndex !== -1 && updates.status !== currentStage?.key) {
          setCandidates((prev) => prev.filter((c) => !ids.includes(c._id)));
          setTotal((n) => Math.max(0, n - ids.length));
        }
      }

      toast.success(ids.length > 1 ? "Candidates updated" : "Candidate updated");
    } catch (e) {
      console.error(e);
      toast.error("Error updating candidate(s)");
    }
  };

  const getStageCount = (statusKey) => {
    return statusCounts.find((s) => s._id === statusKey)?.count || 0;
  };

  const stageDotColor = (stage) => {
    if (stage.key === "SELECTED") return "bg-emerald-500";
    if (stage.key === "REFUSED") return "bg-red-500";
    return "bg-gray-400";
  };

  // Source counts come from /application/aggregate so they reflect the full dataset, not the loaded page.
  const sourceCounts = { all: 0, ad: 0, pool: 0, hunt: 0 };
  for (const row of sourceCountsAgg) {
    const c = row.count || 0;
    sourceCounts.all += c;
    if (row._id === "AD") sourceCounts.ad += c;
    else if (row._id === "POOL") sourceCounts.pool += c;
    else if (row._id === "HUNT") sourceCounts.hunt += c;
  }

  const sourceTabs = [
    { key: "all", label: t("tab_all"), count: sourceCounts.all, dot: null },
    { key: "ad", label: t("tab_ad"), count: sourceCounts.ad, dot: SOURCE_META.AD.dot },
    { key: "pool", label: t("tab_pool"), count: sourceCounts.pool, dot: SOURCE_META.POOL.dot },
    { key: "hunt", label: t("tab_hunt"), count: sourceCounts.hunt, dot: SOURCE_META.HUNT.dot },
  ];

  const handleSourceTabClick = (key) => {
    setSourceFilter(key);
    setSelectedStageIndex(-1);
  };

  const candidatesForView = candidates;

  // Stage counts come from /application/aggregate (full dataset, not the loaded page).
  const stageCounts = stages.map((s) => statusCounts.find((row) => row._id === s.key)?.count || 0);

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Tabs: source filters | stage filters */}
      <div className="sticky top-0 z-10 bg-white px-6 py-4">
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
          {sourceTabs.map((tab) => {
            const active = sourceFilter === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => handleSourceTabClick(tab.key)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors shrink-0 ${
                  active ? "bg-white border border-gray-200 shadow-sm text-gray-900 font-medium" : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {tab.dot && <span className={`w-1.5 h-1.5 rounded-full ${tab.dot}`} />}
                <span>{tab.label}</span>
                <span className={`text-xs ${active ? "text-gray-500" : "text-gray-400"}`}>{tab.count}</span>
              </button>
            );
          })}

          <div className="mx-2 h-5 w-px bg-gray-200 shrink-0" />

          {stages.map((stage, index) => {
            const active = selectedStageIndex === index;
            return (
              <button
                key={stage._id || stage.key || index}
                onClick={() => setSelectedStageIndex(active ? -1 : index)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors shrink-0 ${
                  active ? "bg-white border border-gray-200 shadow-sm text-gray-900 font-medium" : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${stageDotColor(stage)}`} />
                <span>{stage.name}</span>
                <span className={`text-xs ${active ? "text-gray-500" : "text-gray-400"}`}>{stageCounts[index]}</span>
              </button>
            );
          })}

          <button
            onClick={() => navigate(`/recruiter/job-ads/${id}/pipeline`)}
            className="ml-auto p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
            title="Configure pipeline"
          >
            <FiSettings className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Section 2: Content Area */}
      <div ref={scrollRef} className="flex-1 bg-white overflow-y-auto">
        <div className="pr-6 pb-6 pl-6 pt-4">
          <CandidatesView
            candidates={candidatesForView}
            loading={loading}
            currentStage={selectedStageIndex === -1 ? null : currentStage}
            nextStage={selectedStageIndex === -1 ? null : stages[selectedStageIndex + 1]}
            allStages={stages}
            jobId={job?._id}
            onUpdate={handleUpdateCandidates}
            onRefresh={() => {
              fetchPage(1, { reset: true });
              setScreeningPulse((n) => n + 1);
            }}
            screeningPulse={screeningPulse}
            onCandidatePatched={(updated) =>
              setCandidates((prev) => prev.map((c) => (c._id === updated._id ? { ...c, ...updated } : c)))
            }
            onSelectCandidate={(c) => {
              savedScrollRef.current = scrollRef.current?.scrollTop || 0;
              setSelectedApplication(c);
            }}
            onMessageCandidate={(candidateOrCandidates) => {
              setCandidatesToMessage(Array.isArray(candidateOrCandidates) ? candidateOrCandidates : [candidateOrCandidates]);
              setShowMessageModal(true);
            }}
            sort={sort}
            onSortChange={setSort}
            verdictFilter={verdictFilter}
            onVerdictFilterChange={setVerdictFilter}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            total={total}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
            fetchAllMatchingIds={fetchAllMatchingIds}
            scrollContainerRef={scrollRef}
            listKey={queryKey}
          />
        </div>
      </div>

      {/* Candidate Detail Modal */}
      {selectedApplication && selectedCandidateUser && (
        <CandidateModal
          application={selectedApplication}
          setApplication={(app) => {
            if (app?.status && selectedApplication && app.status !== selectedApplication.status) {
              const stageKey = stages[selectedStageIndex]?.key;
              const movedOut = selectedStageIndex !== -1 && app.status !== stageKey;
              setCandidates((list) => (movedOut ? list.filter((c) => c._id !== app._id) : list.map((c) => (c._id === app._id ? { ...c, status: app.status } : c))));
              if (movedOut) setTotal((n) => Math.max(0, n - 1));
              fetchAggregates();
            }
            setSelectedApplication(app);
            if (!app) {
              // Clear URL param when modal closes
              const newParams = new URLSearchParams(searchParams);
              newParams.delete("candidateId");
              setSearchParams(newParams);
              // Restore scroll position from before the modal opened
              if (scrollRef.current) {
                const y = savedScrollRef.current || 0;
                requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: y, behavior: "instant" }));
              }
            }
          }}
          candidate={selectedCandidateUser}
          setCandidate={setSelectedCandidateUser}
          job={job}
          setJob={setJob}
          onNext={() => {
            const nextIndex = candidatesForView.findIndex((c) => c._id === selectedApplication._id) + 1;
            if (nextIndex < candidatesForView.length) {
              setSelectedApplication(candidatesForView[nextIndex]);
            }
          }}
          onPrevious={() => {
            const previousIndex = candidatesForView.findIndex((c) => c._id === selectedApplication._id) - 1;
            if (previousIndex >= 0) {
              setSelectedApplication(candidatesForView[previousIndex]);
            }
          }}
        />
      )}

      {/* Message Candidate Modal */}
      <MessageCandidateModal isOpen={showMessageModal} onClose={() => setShowMessageModal(false)} candidates={candidatesToMessage} />
    </div>
  );
}

function CandidatesView({
  candidates,
  loading,
  currentStage,
  nextStage,
  allStages,
  jobId,
  onUpdate,
  onRefresh,
  onCandidatePatched,
  onSelectCandidate,
  onMessageCandidate,
  sort,
  onSortChange,
  verdictFilter,
  onVerdictFilterChange,
  searchTerm,
  onSearchChange,
  total,
  hasMore,
  loadingMore,
  onLoadMore,
  fetchAllMatchingIds,
  scrollContainerRef,
  screeningPulse,
  listKey,
}) {
  const { t } = useComponentTranslation(translations);
  const [selectedIds, setSelectedIds] = useState([]);
  // When true, bulk actions apply to ALL candidates matching the current server-side filter,
  // not just the rows currently loaded in the DOM.
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);
  const [showStageDropdown, setShowStageDropdown] = useState(false);
  const [candidateDropdownId, setCandidateDropdownId] = useState(null);

  // candidates is already filtered, sorted, paged server-side. Render as-is.
  const visibleCandidates = candidates;

  // Reset selection when filter or stage changes
  useEffect(() => {
    setSelectedIds([]);
    setSelectAllFiltered(false);
  }, [currentStage, searchTerm, verdictFilter, listKey]);

  // Infinite scroll: observe a sentinel just below the last row
  const sentinelRef = useRef(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;
    const root = scrollContainerRef?.current || null;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onLoadMore?.();
      },
      { root, rootMargin: "200px 0px", threshold: 0 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore, scrollContainerRef, visibleCandidates.length]);

  const toggleSelectAll = () => {
    if (selectedIds.length === visibleCandidates.length && visibleCandidates.length > 0 && !selectAllFiltered) {
      setSelectedIds([]);
    } else {
      setSelectedIds(visibleCandidates.map((c) => c._id));
      setSelectAllFiltered(false);
    }
  };

  const handleSelectAllFiltered = () => {
    setSelectAllFiltered(true);
    setSelectedIds(visibleCandidates.map((c) => c._id)); // visual feedback; bulk action expands via fetchAllMatchingIds
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
    setSelectAllFiltered(false);
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((sid) => sid !== id));
      setSelectAllFiltered(false);
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // Resolve the set of IDs to act on. When "select all matching" is active and the
  // user hasn't loaded every row yet, fetch the full ID set from the server.
  const resolveTargetIds = async () => {
    if (selectAllFiltered && total > visibleCandidates.length && typeof fetchAllMatchingIds === "function") {
      const ids = await fetchAllMatchingIds();
      return ids;
    }
    return selectAllFiltered ? visibleCandidates.map((c) => c._id) : selectedIds;
  };

  const handleBulkMove = async (targetStatus) => {
    const ids = await resolveTargetIds();
    if (!ids.length) return;
    onUpdate(ids, { status: targetStatus });
    handleClearSelection();
  };

  const handleExport = async () => {
    // Export functionality — when "all matching" is selected, fetch the full set first.
    let exportSet = candidates.filter((c) => selectedIds.includes(c._id));
    if (selectAllFiltered && total > visibleCandidates.length && typeof fetchAllMatchingIds === "function") {
      // For very large exports the ID-only fetch isn't enough, but the loaded chunk
      // carries the full row data the CSV needs. Fall back to the loaded set with a warning.
      toast("Exporting loaded rows only. Scroll to load more for a complete export.", { icon: "ℹ️" });
      exportSet = visibleCandidates;
    }
    const selectedCandidatesData = exportSet;
    const csvContent = [
      ["Name", "Email", "School", "Last Experience", "City", "Country", "Source", "Applied Date"],
      ...selectedCandidatesData.map((c) => [
        c.candidate_name || "-",
        c.candidate_email || "-",
        c.candidate_last_education?.institution || "-",
        c.candidate_last_experience?.company || "-",
        c.candidate_city || "-",
        countryLabel(c.candidate_country) || "-",
        c.source || "-",
        c.created_at ? new Date(c.created_at).toLocaleDateString() : "-",
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `candidates_export_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Export successful");
  };

  return (
    <div className="space-y-2">
      {/* Search, Total & Pagination Bar */}
      <div className="flex items-center justify-between gap-4 py-1">
        {/* Left: Search + Filter */}
        <div className="flex items-center gap-3">
          <div className="relative">
            <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t("search_placeholder")}
              value={searchTerm}
              onChange={(e) => onSearchChange?.(e.target.value)}
              className="w-64 pl-9 pr-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-shadow"
            />
          </div>

          {/* AI Screening Score Filter */}
          <select
            value={verdictFilter || ""}
            onChange={(e) => onVerdictFilterChange(e.target.value || null)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
          >
            <option value="">AI Score: All</option>
            <option value="strong">Score ≥ 80</option>
            <option value="good">Score 60-79</option>
            <option value="weak">Score &lt; 60</option>
          </select>

          <ScreeningProgressBadge jobId={jobId} pulse={screeningPulse} />
        </div>

        {/* Right: Loaded / total counter */}
        <div className="flex items-center gap-4">
          <div className="text-sm font-medium text-gray-700">
            <span className="font-bold">{total ?? visibleCandidates.length}</span>
            <span className="text-gray-400 mx-1.5">·</span>
            <span className="text-gray-500 text-xs">
              {t("candidates_loaded").replace("{loaded}", visibleCandidates.length).replace("{total}", total ?? visibleCandidates.length)}
            </span>
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar (when items selected) */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 animate-fade-in bg-indigo-600 rounded-xl py-2.5 px-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white">
              {selectAllFiltered ? t("all_candidates_selected").replace("{count}", total) : `${selectedIds.length} ${t("selected")}`}
            </span>
            {/* Gmail-style "select all N matching" prompt — only when everything visible is selected and more is available */}
            {!selectAllFiltered && selectedIds.length === visibleCandidates.length && total > visibleCandidates.length && (
              <>
                <span className="text-white/60">|</span>
                <button onClick={handleSelectAllFiltered} className="text-sm font-medium text-white/90 hover:text-white underline transition-colors">
                  {t("select_all_candidates").replace("{count}", total)}
                </button>
              </>
            )}
          </div>

          <button
            onClick={() => {
              // Messaging always uses loaded rows (it needs full candidate data, not just IDs).
              const selectedCandidates = candidates.filter((c) => selectedIds.includes(c._id));
              if (selectAllFiltered && total > visibleCandidates.length) {
                toast("Messaging only the loaded candidates. Scroll to load more for full reach.", { icon: "ℹ️" });
              }
              onMessageCandidate(selectedCandidates);
            }}
            className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 text-sm font-medium transition-colors"
          >
            <span>{t("message_candidate")}</span>
          </button>

          <button onClick={handleExport} className="bg-white text-gray-900 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors flex items-center gap-2">
            <FiDownload className="w-3.5 h-3.5" />
            {t("export")}
          </button>

          <div className="relative">
            <button
              onClick={() => setShowStageDropdown(!showStageDropdown)}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 text-sm font-medium transition-colors"
            >
              <span>{t("move_to")}...</span>
              <FiChevronRight className={`transition-transform ${showStageDropdown ? "rotate-90" : ""}`} />
            </button>

            {showStageDropdown && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowStageDropdown(false)} />
                <div className="absolute left-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                  {allStages
                    ?.filter((stage) => stage.key !== currentStage?.key)
                    .map((stage) => (
                      <button
                        key={stage.key}
                        onClick={() => {
                          handleBulkMove(stage.key);
                          setShowStageDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center justify-between"
                      >
                        <span>{stage.name}</span>
                        {nextStage && stage.key === nextStage.key && <FiArrowRight className="w-4 h-4 text-gray-400" />}
                      </button>
                    ))}
                </div>
              </>
            )}
          </div>

          <ScreeningBulkRun
            filter={{ job_id: jobId, ...(currentStage?.key ? { status: currentStage.key } : {}) }}
            applicationIds={selectedIds}
            selectAll={selectAllFiltered}
            onDone={onRefresh}
          />

          <button onClick={handleClearSelection} className="text-xs text-white hover:text-white/80 underline transition-colors">
            {t("clear_all")}
          </button>
        </div>
      )}

      {/* Candidates Table */}
      <div className="border border-gray-200 rounded-lg overflow-visible">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th scope="col" className="px-3 py-2 text-left w-10">
                <input
                  type="checkbox"
                  checked={visibleCandidates.length > 0 && selectedIds.length === visibleCandidates.length}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                {t("name")}
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-44">
                {t("school")}
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-44">
                {t("last_experience")}
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                {t("city")}
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                {t("country")}
              </th>
              {(() => {
                const isActive = sort.field === "screening_score";
                const cycle = () => {
                  if (!isActive) onSortChange({ field: "screening_score", order: "desc" });
                  else if (sort.order === "desc") onSortChange({ field: "screening_score", order: "asc" });
                  else onSortChange({ field: "created_at", order: "desc" });
                };
                return (
                  <th
                    scope="col"
                    onClick={cycle}
                    className="px-3 py-2 text-center text-xs font-medium text-gray-500 tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 transition-colors select-none"
                    title="Sort by AI score"
                  >
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-100 text-purple-700">
                      ⚡ AI Score
                      {isActive && (sort.order === "desc" ? <FiChevronDown className="w-3 h-3" /> : <FiChevronUp className="w-3 h-3" />)}
                    </div>
                  </th>
                );
              })()}
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                {t("social")}
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                {t("source")}
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32 cursor-pointer hover:bg-gray-100 transition-colors select-none"
                onClick={() => {
                  const newOrder = sort.field === "created_at" && sort.order === "desc" ? "asc" : "desc";
                  onSortChange({ field: "created_at", order: newOrder });
                }}
              >
                <div className="flex items-center gap-1">
                  {t("applied_at")}
                  {sort.field === "created_at" && (sort.order === "desc" ? <FiChevronDown className="w-3 h-3" /> : <FiChevronUp className="w-3 h-3" />)}
                </div>
              </th>
              <th scope="col" className="relative px-3 py-2 w-32">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={11} className="px-3 py-12 text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
                  </div>
                </td>
              </tr>
            ) : visibleCandidates.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-3 py-12 text-center text-gray-500">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <FiUser className="w-8 h-8 text-gray-300" />
                    <p>{t("no_candidates")}</p>
                  </div>
                </td>
              </tr>
            ) : (
              visibleCandidates.map((candidate) => (
                <tr key={candidate._id} className="hover:bg-gray-50 transition-colors group cursor-pointer">
                  <td className="px-3 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(candidate._id)}
                      onChange={() => toggleSelect(candidate._id)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </td>
                  <td className="px-3 py-2 max-w-[192px]" onClick={() => onSelectCandidate(candidate)}>
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-xs shrink-0">
                        {candidate.candidate_name?.charAt(0)}
                      </div>
                      <div className="font-medium text-gray-900 text-sm truncate" title={candidate.candidate_name}>
                        {candidate.candidate_name}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 max-w-[176px]" onClick={() => onSelectCandidate(candidate)}>
                    {candidate.candidate_last_education ? (
                      <div className="text-sm text-gray-900 truncate" title={candidate.candidate_last_education.institution}>
                        {candidate.candidate_last_education.institution}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2 max-w-[176px]" onClick={() => onSelectCandidate(candidate)}>
                    {candidate.candidate_last_experience ? (
                      <div className="text-sm text-gray-900 truncate" title={candidate.candidate_last_experience.company}>
                        {candidate.candidate_last_experience.company}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2 max-w-[128px]" onClick={() => onSelectCandidate(candidate)}>
                    <span className="text-sm text-gray-900 truncate block" title={candidate.candidate_city}>
                      {candidate.candidate_city || <span className="text-gray-400">-</span>}
                    </span>
                  </td>
                  <td className="px-3 py-2 max-w-[128px]" onClick={() => onSelectCandidate(candidate)}>
                    <span className="text-sm text-gray-900 truncate block" title={countryLabel(candidate.candidate_country)}>
                      {countryLabel(candidate.candidate_country) || <span className="text-gray-400">-</span>}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                    <ScreeningCell candidate={candidate} onSelectCandidate={onSelectCandidate} onRefresh={onRefresh} />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1.5">
                      {candidate.candidate_email && (
                        <a
                          href={`mailto:${candidate.candidate_email}`}
                          className="p-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                          title={candidate.candidate_email}
                        >
                          <FiMail className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {candidate.candidate_phone && (
                        <a
                          href={`tel:${candidate.candidate_phone}`}
                          className="p-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                          title={candidate.candidate_phone}
                        >
                          <FiPhone className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {candidate.candidate_linkedin && (
                        <a
                          href={candidate.candidate_linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                          title="LinkedIn"
                        >
                          <FiLinkedin className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {candidate.candidate_github && (
                        <a
                          href={candidate.candidate_github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-md bg-purple-100 text-purple-600 hover:bg-purple-200 transition-colors"
                          title="GitHub"
                        >
                          <FiGithub className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {candidate.candidate_portfolio && (
                        <a
                          href={candidate.candidate_portfolio}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-md bg-green-100 text-green-600 hover:bg-green-200 transition-colors"
                          title="Portfolio"
                        >
                          <FiGlobe className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {candidate.resume_url && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadResume(candidate);
                          }}
                          className="p-1.5 rounded-md bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                          title="Resume"
                        >
                          <FiFileText className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap" onClick={() => onSelectCandidate(candidate)}>
                    <SourceBadge source={candidate.source} />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap" onClick={() => onSelectCandidate(candidate)}>
                    <span className="text-sm text-gray-500">
                      {candidate.created_at || candidate.application_date ? timeAgo(candidate.created_at || candidate.application_date) : "-"}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium relative" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {/* Move to Dropdown */}
                      <div className="relative">
                        <button
                          onClick={() => setCandidateDropdownId(candidateDropdownId === candidate._id ? null : candidate._id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
                        >
                          <span>{t("move_to")}</span>
                          <FiChevronRight className={`w-3.5 h-3.5 transition-transform ${candidateDropdownId === candidate._id ? "rotate-90" : ""}`} />
                        </button>

                        {candidateDropdownId === candidate._id && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setCandidateDropdownId(null)} />
                            <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                              {allStages
                                ?.filter((stage) => stage.key !== currentStage?.key)
                                .map((stage) => (
                                  <button
                                    key={stage.key}
                                    onClick={() => {
                                      onUpdate(candidate._id, { status: stage.key });
                                      setCandidateDropdownId(null);
                                    }}
                                    className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 transition-colors flex items-center justify-between"
                                  >
                                    <span>{stage.name}</span>
                                    {stage.key === nextStage?.key && <FiArrowRight className="w-3.5 h-3.5 text-gray-400" />}
                                  </button>
                                ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Infinite-scroll sentinel + status row */}
        {visibleCandidates.length > 0 && (
          <div ref={sentinelRef} className="py-3 text-center text-xs text-gray-400">
            {loadingMore ? (
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-gray-300 border-t-gray-500" />
                <span>{t("loading_more")}</span>
              </div>
            ) : hasMore ? (
              <span>&nbsp;</span>
            ) : (
              <span>{t("end_of_list")}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MessageCandidateModal({ isOpen, onClose, candidates = [] }) {
  const { t } = useComponentTranslation(translations);
  const { organization } = useStore();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSubject("");
      setMessage("");
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error(t("subject_required"));
      return;
    }
    if (!message.trim()) {
      toast.error(t("message_required"));
      return;
    }

    try {
      setSending(true);
      let successCount = 0;
      let failedCount = 0;

      // Loop through all candidates and send messages
      for (const candidate of candidates) {
        try {
          const { ok } = await api.post("/user/message_candidate", {
            user_id: candidate.candidate_id,
            subject: subject.trim(),
            message: message.trim(),
            organization_id: organization?._id,
            organization_name: organization?.name,
          });

          if (ok) {
            successCount++;
          } else {
            failedCount++;
          }
        } catch (e) {
          console.error(e);
          failedCount++;
        }
      }

      if (successCount > 0) {
        if (candidates.length === 1) {
          toast.success(t("message_sent"));
        } else {
          toast.success(t("messages_sent").replace("{count}", successCount));
        }
      }

      if (failedCount > 0) {
        toast.error(t("error_sending_message") + ` (${failedCount} failed)`);
      }

      if (successCount > 0) {
        onClose();
      }
    } catch (e) {
      console.error(e);
      capture(e);
      toast.error(t("error_sending_message"));
    } finally {
      setSending(false);
    }
  };

  if (candidates.length === 0) return null;

  const recipientText = candidates.length === 1 ? candidates[0]?.candidate_name || candidates[0]?.candidate_email : t("sending_to_multiple").replace("{count}", candidates.length);

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-2xl w-full">
      <div className="pt-8">
        {/* Header */}
        <div className="px-6 pb-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">{t("message_candidate")}</h2>
          <p className="text-sm text-gray-500 mt-1">To: {recipientText}</p>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t("message_subject")}</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter subject..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-shadow"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t("message_body")}</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here..."
              rows={8}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-shadow resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("cancel")}
          </button>
          <LoadingButton
            onClick={handleSend}
            loading={sending}
            className="gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <FiMail className="w-4 h-4" />
            <span>{t("send_message")}</span>
          </LoadingButton>
        </div>
      </div>
    </Modal>
  );
}
